import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Download, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Logo, Loader, StatusBadge } from "@/components/Brand";
import { formatCurrency, formatDate } from "@/lib/format";

export default function QuotationResult() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);

  useEffect(() => {
    api.get(`/quotations/${quoteId}`).then(({ data }) => setQ(data.quotation))
      .catch(() => { toast.error("Quotation not found"); navigate("/dashboard"); });
  }, [quoteId, navigate]);

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("evo_token");
      const res = await fetch(`${API}/quotations/${quoteId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${q.quotation_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  if (!q) return <div className="min-h-screen bg-[#090A0C]"><Loader /></div>;

  return (
    <div className="min-h-screen bg-[#090A0C] text-slate-50">
      <header className="border-b border-slate-800/80">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <Logo size="text-lg" />
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-amber-400" data-testid="result-dashboard-btn">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="evo-fade-up flex items-center gap-3 text-emerald-400">
          <CheckCircle2 className="h-6 w-6" />
          <span className="text-sm font-semibold">Your estimated quotation is ready</span>
        </div>

        <div className="mt-4 rounded-2xl border border-amber-500/30 bg-gradient-to-b from-amber-500/10 to-transparent p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-400">{q.quotation_number}</span>
              <h1 className="mt-1 font-heading text-2xl font-bold sm:text-3xl">{q.project_type_name} {q.category_name}</h1>
              <div className="mt-2"><StatusBadge status={q.status} /></div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-slate-400">Estimated total</p>
              <p className="font-mono text-4xl font-extrabold text-amber-500" data-testid="quotation-total">{formatCurrency(q.final_total)}</p>
            </div>
          </div>
        </div>

        <h2 className="mt-8 font-heading text-lg font-semibold">Quotation breakdown</h2>
        <div className="mt-3 overflow-hidden rounded-2xl border border-slate-800 bg-[#151820]" data-testid="quotation-breakdown-table">
          {q.line_items.map((li, i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-800/60 px-5 py-3.5 last:border-0">
              <span className="pr-3 text-sm text-slate-300">{li.label}</span>
            </div>
          ))}
          {q.admin_adjustment && (
            <div className="flex items-center justify-between border-b border-slate-800/60 px-5 py-3.5 last:border-0">
              <span className="pr-3 text-sm text-amber-300">Adjustment — {q.admin_adjustment.reason}</span>
            </div>
          )}
          <div className="flex items-center justify-between bg-amber-600 px-5 py-4">
            <span className="font-heading font-bold text-slate-950">Total estimated quotation</span>
            <span className="font-mono text-lg font-extrabold text-slate-950">{formatCurrency(q.final_total)}</span>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-slate-800 bg-[#111318] p-5 text-xs leading-relaxed text-slate-400">
          <p className="mb-2 font-semibold text-slate-300">Estimated quotation — please note</p>
          {q.disclaimer}
          <p className="mt-3">Valid until {formatDate(q.expiry_date)}.</p>
        </div>
      </div>
    </div>
  );
}
